import React, { useEffect, useState } from 'react';
import InfiniteScrollBox from '../../InfiniteScroll/InfiniteScrollBox';
import { useOrganisationStore } from 'Global/Organisations/OrganisationsStore';
import { observer } from 'mobx-react';
import If from '../../../If/If';
import { NothingToShow } from './NothingToShow';
import { LottieWaveDotLoader } from '../../Lottie/LottieWaveDotLoader';
import {
	GetFilteredEventSeries,
	EventSeriesWithNextOccurrence,
} from 'Services/Api/_HumanWritten/EventSeriesEntityService';
import { EventSeriesCard } from '../EventCards/EventSeriesCard';
import { SearchStateProps } from '../EventCards/EventCardList';

interface LabelTabProps {
	/** The label ID to filter by. undefined = all experiences. */
	labelId: string | undefined;
	/** If provided, uses parent-managed state (for the "all" tab). */
	experiencesList?: EventSeriesWithNextOccurrence[];
	setExperiencesList?: React.Dispatch<React.SetStateAction<EventSeriesWithNextOccurrence[]>>;
	/** If provided (and no experiencesList), seeds local state (for per-label tabs). */
	initialItems?: EventSeriesWithNextOccurrence[];
	searchState: SearchStateProps | null;
	initialLoading?: boolean;
}

const LabelTab = ({
	labelId,
	experiencesList: externalList,
	setExperiencesList: setExternalList,
	initialItems,
	searchState,
	initialLoading,
}: LabelTabProps) => {
	const orgStore = useOrganisationStore();
	const [internalList, setInternalList] = useState<EventSeriesWithNextOccurrence[]>(initialItems ?? []);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);

	// Sync internal list when initial data finishes loading
	useEffect(() => {
		if (initialItems) {
			setInternalList(initialItems);
		}
	}, [initialLoading]);

	// Use external (parent) state if provided, otherwise local state
	const list = externalList ?? internalList;
	const setList = setExternalList ?? setInternalList;

	const fetchMoreData = async () => {
		const more = await GetFilteredEventSeries(
			labelId,
			list.length,
			5,
			orgStore.selectedOrganisation?.id,
			searchState?.people,
			searchState?.categories,
			searchState?.startDate,
			searchState?.endDate,
		);
		if (more.length < 5) {
			setHasMore(false);
		}
		setList(prevItems => prevItems.concat(more));
	};

	if (initialLoading) {
		return (
			<div className="nothing-to-show__container">
				<LottieWaveDotLoader />
				<p>Fetching experiences...</p>
			</div>
		);
	}

	if (list.length === 0) {
		return <NothingToShow message="No experiences to show you right now." />;
	}

	return (
		<>
			<If condition={list.length > 0}>
				<InfiniteScrollBox
					id={`label-${labelId ?? 'all'}-scrollable-container`}
					items={list}
					hasMore={hasMore}
					fetchMoreData={fetchMoreData}
					renderItem={(item, index) => <EventSeriesCard eventSeries={item} index={index} />}
					loader=""
					endMessage=""
					direction="vertical"
				/>
			</If>
		</>
	);
};

export default observer(LabelTab);
